// [phase11-v1] The Signal module — trading-ui top-level container.
//
// Reads /api/v1/signal/provider-health and /api/v1/signal/candidates over
// same-origin nginx proxy. Polls 7s for health, 30s for candidates, and
// pauses both intervals when document.hidden === true.
//
// Honest-empty / blocked states only. No fabricated probabilities, win
// rates, or P&L fields — those are explicit "Pending future phase"
// placeholders inside SignalPendingPanel.
//
// Marker strings required by /etc/adversyn/trading-ui-markers.json:
//   - "The Signal"       (component title / sidebar entry — formerly Matrix)
//   - "provider-health"  (endpoint path used by signalClient)
//   - "candidates"       (endpoint path used by signalClient)
//   - "phase11-v1"       (feature-flag string for the drift contract)

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import SignalIcon from "../Signal/SignalIcon";  // PHASE12CF canonical Signal icon
import SignalBeginnerGuide from "../SignalBeginnerGuide";  // beginner-help Phase 1
import {
  getSignalBacktest,
  getSignalCandidates,
  getSignalLabels,
  getSignalProviderHealth,
  type SignalBacktestSummary as SignalBacktestSummaryT,
  type SignalCandidatesPayload,
  type SignalLabelsPayload,
  type SignalProviderHealth,
} from "../../lib/signalClient";
import SignalStatusBanner from "./SignalStatusBanner";
import SignalProviderGrid from "./SignalProviderGrid";
import SignalDecisionTable from "./SignalDecisionTable";  // [signal-decisions-v1] PRIMARY decision-first view
import SignalCandidateTable from "./SignalCandidateTable";
import SignalPendingPanel from "./SignalPendingPanel";
import SignalDiagnostics from "./SignalDiagnostics";
import SignalBacktestSummary from "./SignalBacktestSummary";
import InstitutionalCatalystWatch from "./InstitutionalCatalystWatch";  // PHASE12CC
import SignalLanePanel from "../Signal/SignalLanePanel";  // PHASE12CFD market-cap lanes + Outcome Intelligence
import TradeClassificationFramework from "../Signal/TradeClassificationFramework";  // UI-P3 2026-05-30 Trade Classification scorecard section
import LaneRoutingExplainer from "../Signal/LaneRoutingExplainer";  // Phase 12CG-bis-UI 2026-05-31 row-level lane routing
import EarningsCatalystPanel from "./EarningsCatalystPanel";  // [phase-catalyst-ui-v1] earnings catalysts panel
import ExecutiveEndorsementPanel from "./ExecutiveEndorsementPanel";  // [phase-catalyst-ui-v1] executive endorsement panel

const POLL_HEALTH_MS = 7_000;
const POLL_CANDIDATES_MS = 30_000;
// Phase 11B — labels are written once-daily by the 11:00 ET timer; the UI
// polls them on a slow cadence so the operator sees updates without
// hammering the endpoint.
const POLL_LABELS_MS = 120_000;
// Phase 12 — backtest summary is rewritten once-daily at 11:15 ET.
const POLL_BACKTEST_MS = 180_000;

export default function SignalModule() {
  const [health, setHealth] = useState<SignalProviderHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const [cands, setCands] = useState<SignalCandidatesPayload | null>(null);
  const [candsErr, setCandsErr] = useState<string | null>(null);
  const [candsLoading, setCandsLoading] = useState(true);

  const [labels, setLabels] = useState<SignalLabelsPayload | null>(null);
  const [labelsErr, setLabelsErr] = useState<string | null>(null);

  const [backtest, setBacktest] = useState<SignalBacktestSummaryT | null>(null);
  const [backtestErr, setBacktestErr] = useState<string | null>(null);
  const [backtestLoading, setBacktestLoading] = useState(true);

  const mounted = useRef(true);

  const fetchHealth = useCallback(async () => {
    const res = await getSignalProviderHealth();
    if (!mounted.current) return;
    if (res.ok === true) {
      setHealth(res.data);
      setHealthErr(null);
    } else if (res.ok === false) {
      setHealthErr(res.error.message || "unknown");
    }
    setHealthLoading(false);
  }, []);

  const fetchCands = useCallback(async () => {
    const res = await getSignalCandidates();
    if (!mounted.current) return;
    if (res.ok === true) {
      setCands(res.data);
      setCandsErr(null);
    } else if (res.ok === false) {
      setCandsErr(res.error.message || "unknown");
    }
    setCandsLoading(false);
  }, []);

  const fetchLabels = useCallback(async () => {
    const res = await getSignalLabels();
    if (!mounted.current) return;
    if (res.ok === true) {
      setLabels(res.data);
      setLabelsErr(null);
    } else if (res.ok === false) {
      setLabelsErr(res.error.message || "unknown");
    }
  }, []);

  const fetchBacktest = useCallback(async () => {
    const res = await getSignalBacktest();
    if (!mounted.current) return;
    if (res.ok === true) {
      setBacktest(res.data);
      setBacktestErr(null);
    } else if (res.ok === false) {
      setBacktestErr(res.error.message || "unknown");
    }
    setBacktestLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchHealth();
    fetchCands();
    fetchLabels();
    fetchBacktest();

    let h: number | undefined;
    let c: number | undefined;
    let l: number | undefined;
    let b: number | undefined;

    const start = () => {
      if (h === undefined) h = window.setInterval(fetchHealth, POLL_HEALTH_MS);
      if (c === undefined) c = window.setInterval(fetchCands, POLL_CANDIDATES_MS);
      if (l === undefined) l = window.setInterval(fetchLabels, POLL_LABELS_MS);
      if (b === undefined) b = window.setInterval(fetchBacktest, POLL_BACKTEST_MS);
    };
    const stop = () => {
      if (h !== undefined) {
        window.clearInterval(h);
        h = undefined;
      }
      if (c !== undefined) {
        window.clearInterval(c);
        c = undefined;
      }
      if (l !== undefined) {
        window.clearInterval(l);
        l = undefined;
      }
      if (b !== undefined) {
        window.clearInterval(b);
        b = undefined;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Force an immediate refresh when the tab becomes visible again.
        fetchHealth();
        fetchCands();
        fetchLabels();
        fetchBacktest();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [fetchHealth, fetchCands, fetchLabels, fetchBacktest]);

  const onRefresh = () => {
    fetchHealth();
    fetchCands();
    fetchLabels();
    fetchBacktest();
  };

  return (
    <div
      className="app-container space-y-5 pb-12"
      data-testid="signal-module"
      data-signal-flag="phase11-v1"
    >
      {/* Title strip — mirrors the wealth-management landing copy style */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg text-emerald-500">
            <SignalIcon className="h-5 w-5" aria-label="The Signal" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">
              The Signal
            </h1>
            <p className="text-[var(--muted-text)] mt-1 font-medium italic">
              Tradable gainer radar · Premarket · phase11-v1
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="signal-btn-refresh"
          className="flex items-center gap-2 px-3 py-1.5 bg-[var(--card-bg)] border border-[var(--card-border)] hover:border-emerald-500/40 rounded-xl text-xs font-black tracking-wider uppercase text-[var(--app-text)] transition-all"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <SignalBeginnerGuide />
      <SignalStatusBanner data={health} loading={healthLoading} error={healthErr} />
      <SignalProviderGrid data={health} />
      <SignalDecisionTable />
      <SignalLanePanel />
      <SignalCandidateTable data={cands} loading={candsLoading} health={health} labels={labels} />
      <SignalBacktestSummary data={backtest} loading={backtestLoading} error={backtestErr} />
      <InstitutionalCatalystWatch />
      <EarningsCatalystPanel />
      <ExecutiveEndorsementPanel />
      <TradeClassificationFramework />
      <LaneRoutingExplainer />
      <SignalPendingPanel labelsAvailable={Boolean(labels && labels.row_count > 0)} />
      <SignalDiagnostics health={health} candidates={cands} />

      {candsErr && (
        <div
          className="text-[10px] text-amber-600 font-mono"
          data-testid="signal-candidates-error"
        >
          (candidates fetch error: {candsErr})
        </div>
      )}
      {labelsErr && (
        <div
          className="text-[10px] text-amber-600 font-mono"
          data-testid="signal-labels-error"
        >
          (labels fetch error: {labelsErr})
        </div>
      )}
      {backtestErr && (
        <div
          className="text-[10px] text-amber-600 font-mono"
          data-testid="signal-backtest-error"
        >
          (backtest fetch error: {backtestErr})
        </div>
      )}
    </div>
  );
}
